import {
  initializeApp,
  getApps,
  getApp,
  type FirebaseApp,
  type FirebaseOptions,
} from "firebase/app";
import { getAuth, type Auth } from "firebase/auth";
import {
  initializeFirestore,
  type Firestore,
  type FirestoreSettings,
} from "firebase/firestore";
import { getStorage, type FirebaseStorage } from "firebase/storage";

type FirebaseEnvKey =
  | "VITE_FIREBASE_API_KEY"
  | "VITE_FIREBASE_AUTH_DOMAIN"
  | "VITE_FIREBASE_PROJECT_ID"
  | "VITE_FIREBASE_APP_ID"
  | "VITE_FIREBASE_STORAGE_BUCKET"
  | "VITE_FIREBASE_MESSAGING_SENDER_ID";

function readEnv(key: FirebaseEnvKey): string {
  const value = import.meta.env[key];

  if (!value) {
    throw new Error(`Missing Firebase configuration for ${key}. Update your Vite environment variables.`);
  }

  return value;
}

function getFirebaseApp(): FirebaseApp {
  if (getApps().length > 0) {
    return getApp();
  }

  const options: FirebaseOptions = {
    apiKey: readEnv("VITE_FIREBASE_API_KEY"),
    authDomain: readEnv("VITE_FIREBASE_AUTH_DOMAIN"),
    projectId: readEnv("VITE_FIREBASE_PROJECT_ID"),
    appId: readEnv("VITE_FIREBASE_APP_ID"),
    storageBucket: readEnv("VITE_FIREBASE_STORAGE_BUCKET"),
    messagingSenderId: readEnv("VITE_FIREBASE_MESSAGING_SENDER_ID"),
  };

  return initializeApp(options);
}

export const firebaseApp = getFirebaseApp();

let firestoreInstance: Firestore | null = null;
let notifiedLongPolling = false;

export function getFirebaseAuth(): Auth {
  const auth = getAuth(firebaseApp);
  auth.useDeviceLanguage();
  return auth;
}

export function getFirebaseFirestore(): Firestore {
  if (firestoreInstance) {
    return firestoreInstance;
  }

  const longPollingSettings: FirestoreSettings = {
    experimentalAutoDetectLongPolling: true,
  };

  firestoreInstance = initializeFirestore(firebaseApp, longPollingSettings);

  if (!notifiedLongPolling) {
    console.warn(
      "[Firestore] Auto-detect long polling fallback enabled; realtime streams may be blocked by network filters.",
    );
    notifiedLongPolling = true;
  }

  return firestoreInstance;
}

export function getFirebaseStorage(): FirebaseStorage {
  return getStorage(firebaseApp);
}
